import { useState, useEffect, useCallback } from 'react';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';
import { getBackendUrl } from '@/hooks/use-get-backend-url';

interface UseAssetImageResult {
  imageUrl: string | null;
  isLoading: boolean;
  error: boolean;
}

function getApiBaseUrl(): string {
  const apiUrlFromConfig = getApiUrl();
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  const backendUrl = getBackendUrl();
  const isE2B = typeof window !== 'undefined' && window.location.hostname.includes('.e2b.app');

  const baseUrl = isE2B ? backendUrl : (apiUrlFromConfig ?? envUrl ?? backendUrl);
  return baseUrl.replace(/\/$/, '');
}

async function buildHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  const authConfig = getAuthConfig();
  if (authConfig.getAccessToken) {
    try {
      const token = await authConfig.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting access token for image:', error);
    }
  }

  headers['X-Instance-Id'] = authConfig.instanceId ?? 'default-instance';
  return headers;
}

const imageCache = new Map<string, string>();

export function useAssetImage(primaryImageUrl: string | undefined): UseAssetImageResult {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const fetchImage = useCallback(async (url: string) => {
    if (imageCache.has(url)) {
      setImageUrl(imageCache.get(url)!);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(false);

    try {
      const baseUrl = getApiBaseUrl();
      const fullUrl = url.startsWith('/') ? `${baseUrl}${url}` : url;
      const headers = await buildHeaders();

      const response = await fetch(fullUrl, { headers });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      imageCache.set(url, blobUrl);
      setImageUrl(blobUrl);
    } catch (err) {
      console.error('Error fetching asset image:', err);
      setError(true);
      setImageUrl(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!primaryImageUrl) {
      setImageUrl(null);
      setIsLoading(false);
      setError(false);
      return;
    }

    if (primaryImageUrl.startsWith('data:')) {
      setImageUrl(primaryImageUrl);
      setIsLoading(false);
      setError(false);
      return;
    }

    fetchImage(primaryImageUrl);
  }, [primaryImageUrl, fetchImage]);

  return { imageUrl, isLoading, error };
}
